import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useIntl } from 'react-intl';
import PropTypes from 'prop-types';
import { bem } from 'helpers/class-name';
import { makePath } from 'helpers/link-generation';
import Segment from 'helpers/segment';
import { openDecagonChat } from 'components/Decagon';
import Message from 'components/Message';
import Button from 'components/Button';
import { CardSection } from 'components/Card';
import { closeModal } from 'components/Modal';
import Isvg from 'react-inlinesvg';
import { ICONS } from 'constants/icons';
import { intlMessage } from 'constants/propTypes';
import appMessages from 'features/App/App.messages';
import { selectUserInfo } from 'selectors/general';
import { REPORT_TYPE_DISRUPTIVE, REPORT_TYPE_TRIAL } from '../ReservationFeedbackModalV2.constants';
import messages from '../ReservationFeedbackModalV2.messages';
import UserProfile from '../UserProfile';

import './ReasonSelection.scss';

import ReasonCard, { REASONS } from './ReasonCard';

const { element } = bem('reason-selection');

const ReasonSelection = ({
  fullName,
  profileUrl,
  venueId,
  reservationId,
  shouldShowTrialAbuseReason,
  disruptiveUserDescription,
  setDisruptiveUserReportDetailsStep,
  setTrialAbuseReportDetailsStep,
  disruptiveUserAlreadyReported,
  trialAbuseAlreadyReported,
}) => {
  const dispatch = useDispatch();
  const intl = useIntl();
  const { locale } = useSelector(selectUserInfo);
  const closeAriaLabel = intl.formatMessage(appMessages.close);

  const handleDisruptiveSelect = () => {
    Segment.event('Click Report Type', {
      Type: REPORT_TYPE_DISRUPTIVE,
      VID: venueId,
      ReservationID: reservationId,
    });
    setDisruptiveUserReportDetailsStep();
  };

  const handleTrialSelect = () => {
    Segment.event('Click Report Type', {
      Type: REPORT_TYPE_TRIAL,
      VID: venueId,
      ReservationID: reservationId,
    });
    setTrialAbuseReportDetailsStep();
  };

  return (
    <div className={element('reason-selection-wrapper')}>
      <CardSection flush className={element('header')}>
        <div className={element('title-wrapper')}>
          <Message as="h1" message={messages.reasonSelectionTitle} />
          <div className={element('close-button')}>
            <button
              type="button"
              onClick={() => dispatch(closeModal())}
              name="close-button"
              aria-label={closeAriaLabel}
              className={element('close-icon')}>
              <Isvg src={ICONS.cross} />
            </button>
          </div>
        </div>
        <UserProfile fullName={fullName} profileUrl={profileUrl} />
      </CardSection>

      <CardSection flush className={element('content')}>
        <div className={element('reason-selection')}>
          <p className={element('reason-selection-instruction')}>
            <Message message={messages.selectReason} />
          </p>
          <div className={element('reason-cards')}>
            <ReasonCard
              reason={REASONS.DISRUPTIVE_USER}
              title={messages.disruptiveUser}
              description={disruptiveUserDescription}
              isAlreadyReported={disruptiveUserAlreadyReported}
              onSelect={handleDisruptiveSelect}
            />
            {shouldShowTrialAbuseReason && (
              <ReasonCard
                reason={REASONS.TRIAL_PROGRAM_ABUSE}
                title={messages.trialProgramAbuse}
                description={messages.trialProgramAbuseDescription}
                isAlreadyReported={trialAbuseAlreadyReported}
                onSelect={handleTrialSelect}
              />
            )}
          </div>
          <p className={element('reason-selection-footer')}>
            <Message
              message={messages.footerText}
              values={{
                a: text => (
                  <a
                    href={makePath.reportUserFAQs(locale)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={element('footer-link')}
                    onClick={() => Segment.event('Click Report Learn More', { VID: venueId, RID: reservationId })}>
                    {text}
                  </a>
                ),
                contactUs: text => (
                  <Button styled="inline-link" onClick={openDecagonChat}>
                    {text}
                  </Button>
                ),
              }}
            />
          </p>
        </div>
      </CardSection>
    </div>
  );
};

ReasonSelection.propTypes = {
  fullName: PropTypes.string.isRequired,
  profileUrl: PropTypes.string,
  venueId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  reservationId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  shouldShowTrialAbuseReason: PropTypes.bool.isRequired,
  disruptiveUserDescription: intlMessage.isRequired,
  setDisruptiveUserReportDetailsStep: PropTypes.func.isRequired,
  setTrialAbuseReportDetailsStep: PropTypes.func.isRequired,
  disruptiveUserAlreadyReported: PropTypes.bool,
  trialAbuseAlreadyReported: PropTypes.bool,
};

export default ReasonSelection;
